import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Grid3x3, Plus, Loader2, Star, BarChart3, Share2, MousePointerClick, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { useRoomFavorites } from '../configurator/useRoomFavorites';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/fabrics`;
const abs = (u) => (u?.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const thumb = (u, w = 160) => (u?.startsWith('/api/storage/files/') ? `${process.env.REACT_APP_BACKEND_URL}/api/share/thumb/${u.replace('/api/storage/files/', '')}?w=${w}` : abs(u));

/* Start a "fill missing" batch job after confirming the cost estimate */
const useFillJob = (headers, onDone) => {
  const [job, setJob] = useState(null);
  const poll = useRef(null);
  useEffect(() => () => clearInterval(poll.current), []);

  const fill = async (body, what) => {
    try {
      const { data: est } = await axios.post(`${API}/colors/regenerate-all/estimate`, body, { headers });
      if (!est.scene_count) { toast.info('Nothing missing'); return; }
      if (!window.confirm(`Generate ${est.scene_count} scene(s) for ${what}?\nEstimated cost ≈ $${est.estimated_cost_usd} · ~${est.estimated_minutes} min`)) return;
      const { data } = await axios.post(`${API}/colors/regenerate-all`, body, { headers });
      setJob({ total: data.total, completed: 0, status: 'running' });
      toast.success(`Generating ${data.total} scene(s)…`);
      poll.current = setInterval(async () => {
        try {
          const { data: j } = await axios.get(`${API}/visualization-jobs/${data.job_id}`, { headers });
          setJob({ total: data.total, completed: j.completed || 0, status: j.status });
          if (j.status !== 'running') { clearInterval(poll.current); toast.success('Fill complete'); onDone(); }
        } catch { /* keep polling */ }
      }, 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start generation');
    }
  };
  return { job, fill };
};

const Cell = ({ url, isDefault, onFill, busy, testId }) => (url ? (
  <a href={abs(url)} target="_blank" rel="noreferrer" className="relative block w-16 h-11 rounded overflow-hidden border border-neutral-200" data-testid={testId}>
    <img src={thumb(url)} alt="" className="w-full h-full object-cover" loading="lazy" />
    {isDefault && <Star className="absolute top-0.5 right-0.5 w-3 h-3 fill-amber-400 text-amber-500" />}
  </a>
) : (
  <button type="button" onClick={onFill} disabled={busy} title="Generate this scene"
    className="w-16 h-11 rounded border border-dashed border-neutral-300 text-neutral-400 hover:border-amber-500 hover:text-amber-600 hover:bg-amber-50 flex items-center justify-center transition-colors disabled:opacity-40"
    data-testid={testId}>
    <Plus className="w-4 h-4" />
  </button>
));

/* Colour × room matrix for one collection with one-click fill */
export const RoomCoverageDialog = ({ allColors, token }) => {
  const [open, setOpen] = useState(false);
  const [collection, setCollection] = useState('');
  const [grid, setGrid] = useState(null);
  const [extra, setExtra] = useState([]);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const { favorites } = useRoomFavorites(token);
  const collections = [...new Set(allColors.map(c => c.collection_name).filter(Boolean))].sort();

  const load = useCallback(() => {
    if (!collection) return;
    axios.get(`${API}/collections/room-grid`, { params: { collection_name: collection }, headers })
      .then(({ data }) => setGrid(data)).catch(() => toast.error('Could not load coverage'));
  }, [collection, headers]);
  useEffect(() => { setGrid(null); setExtra([]); load(); }, [load]);
  const { job, fill } = useFillJob(headers, load);
  const busy = job?.status === 'running';

  const columns = grid ? [...new Set([...grid.used_rooms, ...favorites, ...extra])].filter(r => grid.room_types.some(t => t.id === r)) : [];
  const label = (id) => grid?.room_types.find(t => t.id === id)?.label || id;
  const missing = (room) => grid.colors.filter(c => !c.rooms[room]).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2" data-testid="room-coverage-btn">
          <Grid3x3 className="w-4 h-4" /> Room Coverage
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[95vw] w-[1200px]">
        <DialogHeader><DialogTitle>Room Style Coverage</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <select className="rounded border border-neutral-300 p-2 text-sm" value={collection} onChange={e => setCollection(e.target.value)} data-testid="room-coverage-collection-select">
            <option value="">Select collection...</option>
            {collections.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          {grid && (
            <select className="rounded border border-neutral-300 p-2 text-sm" value="" onChange={e => e.target.value && setExtra(x => [...x, e.target.value])} data-testid="room-coverage-add-column">
              <option value="">+ Add room column...</option>
              {grid.room_types.filter(t => !columns.includes(t.id)).map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          )}
          {job && (
            <span className="ml-auto flex items-center gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-3 py-1" data-testid="room-coverage-job">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {job.completed}/{job.total} generated
            </span>
          )}
        </div>
        {grid && (
          <div className="max-h-[65vh] overflow-auto border border-neutral-200 rounded-lg" data-testid="room-coverage-grid">
            <table className="text-xs border-collapse">
              <thead className="sticky top-0 bg-white z-10 shadow-sm">
                <tr>
                  <th className="sticky left-0 bg-white text-left p-2 min-w-[180px]">{grid.colors.length} colors</th>
                  {columns.map(room => (
                    <th key={room} className="p-2 font-medium text-neutral-700 min-w-[84px] align-bottom">
                      <div className="flex items-center gap-1">{favorites.includes(room) && <Star className="w-3 h-3 fill-amber-400 text-amber-500" />}{label(room)}</div>
                      <div className="text-[10px] font-normal text-neutral-400">{grid.colors.length - missing(room)}/{grid.colors.length}</div>
                      {missing(room) > 0 && (
                        <button type="button" disabled={busy} onClick={() => fill({ collection_name: collection, room_types: [room], mode: 'missing' }, `${label(room)} · ${collection}`)}
                          className="mt-1 text-[10px] text-amber-700 hover:underline disabled:opacity-40" data-testid={`room-coverage-fill-${room}`}>
                          Fill {missing(room)} missing
                        </button>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grid.colors.map(c => (
                  <tr key={c.id} className="border-t border-neutral-100" data-testid={`room-coverage-row-${c.id}`}>
                    <td className="sticky left-0 bg-white p-2">
                      <div className="flex items-center gap-2">
                        {c.swatch_url ? <img src={thumb(c.swatch_url, 64)} alt="" className="w-7 h-7 rounded object-cover" loading="lazy" /> : <span className="w-7 h-7 rounded border" style={{ background: c.hex_color || '#ddd' }} />}
                        <span className="truncate max-w-[140px]" title={c.code}>{c.name}</span>
                      </div>
                    </td>
                    {columns.map(room => (
                      <td key={room} className="p-1.5">
                        <Cell url={c.rooms[room]} isDefault={c.default_room === room} busy={busy} testId={`room-coverage-cell-${c.id}-${room}`}
                          onFill={() => fill({ collection_name: collection, room_types: [room], mode: 'missing', color_ids: [c.id] }, `${c.name} · ${label(room)}`)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Bar = ({ value, max, color }) => (
  <div className="h-1.5 bg-neutral-100 rounded-full w-full"><div className={`h-1.5 rounded-full ${color}`} style={{ width: `${max ? (value / max) * 100 : 0}%` }} /></div>
);

/* Which room styles shoppers pick and share most */
export const RoomInsightsDialog = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(() => {
    axios.get(`${API}/room-analytics`, { params: { days }, headers }).then(({ data: d }) => setData(d)).catch(() => toast.error('Could not load insights'));
  }, [days, headers]);
  useEffect(() => { if (open) { setData(null); load(); } }, [open, load]);
  const { job, fill } = useFillJob(headers, load);
  const busy = job?.status === 'running';
  const fillRoom = (r) => fill({ room_types: [r.room], mode: 'missing' }, `${r.label} · every color missing it`);
  const top = data?.rooms.find(r => (r.pick || r.share) && r.colors_with_scene < data.total_colors);

  const maxPick = Math.max(1, ...(data?.rooms || []).map(r => r.pick));
  const maxShare = Math.max(1, ...(data?.rooms || []).map(r => r.share));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2" data-testid="room-insights-btn">
          <BarChart3 className="w-4 h-4" /> Room Insights
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Most Picked &amp; Shared Room Styles</DialogTitle></DialogHeader>
        <div className="flex gap-1.5" data-testid="room-insights-range">
          {[7, 30, 90].map(d => (
            <button key={d} type="button" onClick={() => setDays(d)} className={`px-3 py-1 rounded-full text-xs border ${days === d ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-200 text-neutral-600'}`} data-testid={`room-insights-days-${d}`}>Last {d} days</button>
          ))}
        </div>
        {data && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="room-insights-fill-top">
            {top ? (
              <>
                <p className="text-sm text-amber-900 flex-1">Shoppers' favourite: <b>{top.label}</b> — {data.total_colors - top.colors_with_scene} colors don't have it yet.</p>
                <Button size="sm" disabled={busy} onClick={() => fillRoom(top)} data-testid="room-insights-fill-top-btn">Fill {top.label} for all</Button>
              </>
            ) : <p className="text-sm text-amber-900">No shopper favourites with missing scenes yet.</p>}
            {job && <span className="text-xs text-blue-700 flex items-center gap-1" data-testid="room-insights-job">{busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{job.completed}/{job.total} generated</span>}
          </div>
        )}
        {!data ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-8" /> : (
          <div className="max-h-[60vh] overflow-y-auto space-y-5">
            <table className="w-full text-sm" data-testid="room-insights-table">
              <thead><tr className="text-[11px] uppercase text-neutral-500 text-left">
                <th className="py-1">Room style</th><th className="w-40"><MousePointerClick className="inline w-3 h-3" /> Picks</th>
                <th className="w-40"><Share2 className="inline w-3 h-3" /> Shares</th><th className="w-20"><ExternalLink className="inline w-3 h-3" /> Opens</th><th className="w-28">Colors with it</th><th className="w-24" />
              </tr></thead>
              <tbody>
                {data.rooms.map(r => (
                  <tr key={r.room} className="border-t border-neutral-100" data-testid={`room-insights-row-${r.room}`}>
                    <td className="py-2 pr-2 font-medium">{r.label}</td>
                    <td className="pr-3"><div className="flex items-center gap-2"><span className="w-8 text-right tabular-nums">{r.pick}</span><Bar value={r.pick} max={maxPick} color="bg-amber-500" /></div></td>
                    <td className="pr-3"><div className="flex items-center gap-2"><span className="w-8 text-right tabular-nums">{r.share}</span><Bar value={r.share} max={maxShare} color="bg-emerald-500" /></div></td>
                    <td className="tabular-nums">{r.open}</td>
                    <td className={`tabular-nums ${(r.pick || r.share) && r.colors_with_scene < data.total_colors / 4 ? 'text-red-600 font-semibold' : 'text-neutral-600'}`}>{r.colors_with_scene}/{data.total_colors}</td>
                    <td>{r.colors_with_scene < data.total_colors && (
                      <button type="button" disabled={busy} onClick={() => fillRoom(r)} className="text-[11px] text-amber-700 hover:underline disabled:opacity-40" data-testid={`room-insights-fill-${r.room}`}>Fill {data.total_colors - r.colors_with_scene}</button>
                    )}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-neutral-400">Red = shoppers like it but fewer than 25% of colors have it — a good one to generate next.</p>
            {data.top_shared.length > 0 && (
              <div data-testid="room-insights-top-shared">
                <p className="text-xs font-semibold uppercase text-neutral-500 mb-1.5">Most shared looks</p>
                {data.top_shared.map(t => (
                  <div key={`${t.color_id}-${t.room}`} className="flex justify-between text-sm py-1 border-t border-neutral-100">
                    <span>{t.color_name || t.color_id} <span className="text-neutral-400">· {t.collection_name} · {(t.room || 'default').replace(/_/g, ' ')}</span></span>
                    <span className="tabular-nums">{t.shares}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
